import { useMemo } from 'react'
import type { ConnectedSummaryLine } from '../../../../../components/ConnectedSummaryLines'
import { ConnectedSummaryLinesV2 } from '../../../../../components/ConnectedSummaryLinesV2'
import { convertEpochToDate } from '../../../../../../../utils/utils'
import {
  isListPlansFetchFailed,
  isListPlansHeaderLoading,
  useLumpsumPlansContext
} from '../../LumpsumPlansProvider'
import { useBottomSheet } from '../../../../../../../state-management/contexts/BottomSheetContext'
import { EventNames } from '../../../../../../../types/misc.types'
import { lightImpactVibrate, postCTA, trackEvent } from '../../../../../../../utils/appBridge'
import type { LumpsumAllocationPlansResponse } from '../../../api/allocation.types'
import { PlansLumpsumListEventIds, PlansScreen } from '../../../../analytics/plansEvents'
import { AllocationPlanInfoBottomSheet } from './AllocationPlanInfoBottomSheet'
import { AllocationPlanUpdatedBottomSheet } from './AllocationPlanUpdatedBottomSheet'
import {
  ListPlansPlanUpdatedBanner,
  ListPlansPlanUpdatedBannerType,
  shouldShowPlanUpdatedBanner
} from '../common/ListPlansPlanUpdatedBanner'
import { HeaderInfoAfterWord } from '../common/ListPlansHeaderShared'
import { HeaderHeroDescription } from '../../lumpsum-ui-components/HeaderHeroDescription'
import { HeaderHeroSkeleton } from '../../lumpsum-ui-components/HeaderHeroSkeleton'
import { HeaderRiveBackdrop } from '../common/HeaderRiveBackdrop'
import {
  getPlansFlowTheme,
  PlansThemeFlow,
  resolveHeaderAppearanceWithRiskProfile,
  type LumpsumHeaderAppearance
} from '../common/riskProfileHeaderTheme'
import type { AllocationRiskProfile } from '../../../../../expert-review/types/allocation.types'

import { PlansPlanMode } from '../../../api/types'
import { isPlansListFullyEmpty } from '../../../../utils/lumpsum/planListUtils'

const ALLOCATION_PLAN_DESCRIPTION =
  'This plan optimises allocation, performance, diversification, overlaps and more, in a tax-efficient manner'
const ALLOCATION_TOP_SHAPE_STATUS_COPY =
  'No changes are needed this month. We’ll review it again next month to make sure it stays that way.'

export function LumpsumAllocationHeader() {
  const { planMode, allocationPlansData, allocationPlansQuery } = useLumpsumPlansContext()
  const headerAppearance = resolveAllocationHeaderAppearance(allocationPlansData?.riskProfile)
  const isHeaderActive = planMode === PlansPlanMode.ALLOCATION
  const isHeaderLoading = isListPlansHeaderLoading(
    PlansPlanMode.ALLOCATION,
    allocationPlansData != null,
    allocationPlansQuery
  )
  const isPlansEmpty = !isHeaderLoading && isPlansListFullyEmpty(allocationPlansData?.categories)
  const isPlansFetchFailed = isListPlansFetchFailed(PlansPlanMode.ALLOCATION, allocationPlansQuery)
  const isSubscriptionEligible = allocationPlansData?.isPlanSubscriptionEligible === true
  const shouldHideForEmptyList = isPlansEmpty && isSubscriptionEligible
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const plansListHero = useMemo(
    () => getAllocationPlansListHeroFields(allocationPlansData),
    [allocationPlansData]
  )

  if (shouldHideForEmptyList || isPlansFetchFailed) {
    return null
  }

  function handleOptimisedInfoClick() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.LumpsumAllocation,
        eventId: PlansLumpsumListEventIds.OptimisedInfo,
        buttonLabel: 'Optimised info'
      }
    })
    const coreBottomsheetCta = allocationPlansData?.coreBottomsheetCta
    if (coreBottomsheetCta == null) return
    postCTA(JSON.stringify(coreBottomsheetCta))
  }

  function handleStrategyLandingCtaClick() {
    const strategyLandingCta = allocationPlansData?.strategyLandingCta
    if (strategyLandingCta == null) return
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.LumpsumAllocation,
        eventId: PlansLumpsumListEventIds.StrategyLandingCta,
        buttonLabel: 'Strategy landing'
      }
    })
    postCTA(JSON.stringify(strategyLandingCta))
  }

  function handleAllocationPlanInfoClick() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.LumpsumAllocation,
        eventId: PlansLumpsumListEventIds.PlanInfo,
        buttonLabel: 'Plan info'
      }
    })
    openBottomSheet(<AllocationPlanInfoBottomSheet onDismiss={closeBottomSheet} />)
  }

  function handleSeeWhatChangedClick() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.LumpsumAllocation,
        eventId: PlansLumpsumListEventIds.SeeWhatChanged,
        buttonLabel: 'See what changed'
      }
    })
    openBottomSheet(
      <AllocationPlanUpdatedBottomSheet
        planSnapshotDate={allocationPlansData?.planSnapshotDate ?? 0}
        planDurationInMonths={allocationPlansData?.planDurationInMonths}
        newRecommendationsCount={allocationPlansData?.newRecommendationsCount}
        updatedRecommendationsCount={allocationPlansData?.updatedRecommendationsCount}
        invalidatedRecommendationsCount={allocationPlansData?.invalidatedRecommendationsCount}
        onDismiss={closeBottomSheet}
      />
    )
  }

  const connectedSummaryLines = isHeaderLoading
    ? []
    : getAllocationConnectedSummaryLines(plansListHero, handleOptimisedInfoClick)
  const showPlanUpdatedBanner =
    !isHeaderLoading &&
    shouldShowPlanUpdatedBanner(allocationPlansData?.planCount, allocationPlansData?.planSnapshotDate)

  return (
    <div>
      {isHeaderLoading ? (
        <HeaderHeroSkeleton />
      ) : (
        <>
          {showPlanUpdatedBanner ? (
            <ListPlansPlanUpdatedBanner
              bannerType={ListPlansPlanUpdatedBannerType.ALLOCATION}
              planSnapshotDate={allocationPlansData?.planSnapshotDate ?? 0}
              planDurationInMonths={allocationPlansData?.planDurationInMonths}
              onSeeWhatChangedClick={handleSeeWhatChangedClick}
            />
          ) : null}
          <div
            className={`relative overflow-visible px-5${plansListHero.isTopShape && !isSubscriptionEligible ? ' pb-8' : ' pb-1'}${showPlanUpdatedBanner ? '' : ' pt-6'}`}
          >
            <HeaderRiveBackdrop
              isActive={isHeaderActive}
              riveSrc={headerAppearance.riveSrc}
              headerSvgSrc={headerAppearance.headerSvgSrc}
              backgroundColor={headerAppearance.backgroundColor}
              onLandingCtaClick={
                allocationPlansData?.strategyLandingCta == null
                  ? undefined
                  : handleStrategyLandingCtaClick
              }
            />

            <div className="relative z-[1] flex gap-3 pointer-events-none">
              <div className="min-w-0 flex-1 max-w-[calc(100%-130px)] pointer-events-auto">
                {plansListHero.reviewedOnLabel.length > 0 ? (
                  <div className={headerAppearance.reviewedOnClassName}>{plansListHero.reviewedOnLabel}</div>
                ) : null}

                <div className="pt-1 whitespace-pre-line text-[22px] font-[500] leading-[130%] text-[#232323]">
                  {plansListHero.headline}
                </div>

                {connectedSummaryLines.length > 0 ? (
                  <div className="pt-3 pl-[2px]">
                    <ConnectedSummaryLinesV2
                      lines={connectedSummaryLines}
                      connectorColor={headerAppearance.connectorColor}
                      connectorThickness={2}
                      gap={4}
                    />
                  </div>
                ) : null}

                {plansListHero.isTopShape ? (
                  <p className="pt-3 text-[13px] font-[400] leading-[150%] text-[#3D3D3D]">
                    {ALLOCATION_TOP_SHAPE_STATUS_COPY}
                  </p>
                ) : null}
              </div>
            </div>

            <HeaderHeroDescription
              description={ALLOCATION_PLAN_DESCRIPTION}
              onLearnMoreClick={handleAllocationPlanInfoClick}
              className="relative z-[1] w-full max-w-[300px]"
            />
          </div>
        </>
      )}
    </div>
  )
}

type AllocationPlansListHeroFields = {
  reviewedOnLabel: string
  headline: string
  isTopShape: boolean
  sellFundCount: number
  buyFundCount: number
  underReviewFundCount: number
  includedFundCount: number
  idealFundCount: number
}

function getAllocationPlansListHeroFields(
  allocationPlansData: LumpsumAllocationPlansResponse | undefined
): AllocationPlansListHeroFields {
  const actionableFundCount = allocationPlansData?.actionableFundCount ?? 0
  const isTopShape = actionableFundCount === 0

  return {
    reviewedOnLabel: formatAllocationPlansReviewedOnLabel(allocationPlansData?.planSnapshotDate),
    headline: isTopShape ? 'Your portfolio is in top shape' : 'Allocation Plan',
    isTopShape,
    sellFundCount: allocationPlansData?.sellFundCount ?? 0,
    buyFundCount: allocationPlansData?.buyFundCount ?? 0,
    underReviewFundCount: allocationPlansData?.underReviewFundCount ?? 0,
    includedFundCount: allocationPlansData?.includedFundCount ?? 0,
    idealFundCount: allocationPlansData?.idealFundCount ?? 0
  }
}

function getAllocationConnectedSummaryLines(
  plansListHero: AllocationPlansListHeroFields,
  onOptimisedInfoClick: () => void
): ConnectedSummaryLine[] {
  const lines: ConnectedSummaryLine[] = []

  if (plansListHero.isTopShape) {
    if (plansListHero.underReviewFundCount > 0) {
      lines.push({
        html: `<span class="font-[500] text-[#3d3d3d]">${formatFundCountLabel(plansListHero.underReviewFundCount)}</span> to sell later`
      })
    }

    if (plansListHero.idealFundCount > 0) {
      lines.push({
        html: `Maintain <span class="font-[500] text-[#3d3d3d]">${formatFundCountLabel(plansListHero.idealFundCount)}</span>`
      })
    }

    lines.push({
      content: (
        <>
          <span className="font-[500] text-[#3d3d3d]">Optimised</span> for core{' '}
          <HeaderInfoAfterWord word="plan" />
        </>
      ),
      onClick: onOptimisedInfoClick
    })

    return lines
  }

  if (plansListHero.sellFundCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatFundCountLabel(plansListHero.sellFundCount)}</span> to sell`
    })
  }

  if (plansListHero.buyFundCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatFundCountLabel(plansListHero.buyFundCount)}</span> to buy`
    })
  }

  if (plansListHero.underReviewFundCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatFundCountLabel(plansListHero.underReviewFundCount)}</span> to sell later`
    })
  }

  const fundCountChangeLine = getAllocationFundCountChangeSummaryLine(
    plansListHero.includedFundCount,
    plansListHero.idealFundCount
  )
  if (fundCountChangeLine != null) {
    lines.push(fundCountChangeLine)
  }

  lines.push({
    content: (
      <>
        <span className="font-[500] text-[#3d3d3d]">Optimised</span> for core{' '}
        <HeaderInfoAfterWord word="portfolio" />
      </>
    ),
    onClick: onOptimisedInfoClick
  })

  return lines
}

function getAllocationFundCountChangeSummaryLine(
  includedFundCount: number,
  idealFundCount: number
): ConnectedSummaryLine | null {
  if (includedFundCount <= 0 || idealFundCount <= 0 || includedFundCount === idealFundCount) {
    return null
  }

  if (includedFundCount > idealFundCount) {
    return {
      html: `<span class="font-[500] text-[#3d3d3d]">Consolidate</span> ${includedFundCount} funds → ${idealFundCount} funds`
    }
  }

  return {
    html: `<span class="font-[500] text-[#3d3d3d]">Diversify</span> ${includedFundCount} funds → ${idealFundCount} funds`
  }
}

function formatFundCountLabel(fundCount: number): string {
  return `${fundCount} fund${fundCount === 1 ? '' : 's'}`
}

function formatAllocationPlansReviewedOnLabel(planSnapshotDate?: number | null): string {
  if (!planSnapshotDate) {
    return ''
  }
  return `Reviewed on ${convertEpochToDate(planSnapshotDate, '%_d %b %Y')}`
}

const ALLOCATION_FLOW_THEME = getPlansFlowTheme(PlansThemeFlow.ALLOCATION)
const ALLOCATION_HEADER_STYLE = {
  backgroundColor: ALLOCATION_FLOW_THEME.topColor,
  chromeBackground: ALLOCATION_FLOW_THEME.chromeBackground,
  reviewedOnClassName: ALLOCATION_FLOW_THEME.periodLabelClassName,
  connectorColor: ALLOCATION_FLOW_THEME.connectorColor
}

export function resolveAllocationHeaderAppearance(
  riskProfile?: AllocationRiskProfile | null
): LumpsumHeaderAppearance {
  return resolveHeaderAppearanceWithRiskProfile({
    riskProfile,
    strategyPalette: 'allocation',
    ...ALLOCATION_HEADER_STYLE
  })
}
